import { useEffect, useRef, useState } from 'react';
import { CircleDollarSign, Info, Search } from 'lucide-react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { ReactComponent as Screw } from '../assets/icon/screw.svg';
import { ReactComponent as Graph } from '../assets/icon/graph.svg';
import { ReactComponent as BriefCase } from '../assets/icon/briefCase.svg';
import { ReactComponent as Calculator } from '../assets/icon/calculator.svg';
import { ReactComponent as Calender } from '../assets/icon/calender.svg';
import PlayGroundCard from '../components/playGroundCard';
import GraphSection from './GraphSection';
import TableData from '../data/TableData.json';
import filterIcon from '../assets/icon/filterIcon.svg';
import calenderIcon from '../assets/icon/calenderIcon.svg';
import FilterForm from '../components/SystemAnalytics/FilterForm';
import {
  filterDropdownData,
  fetchCostAnalyticsData
  // filterDropdownDataCloudProvider
} from '../service/apiServices';
import { useAzureData } from '../context/AzureContext';
import DataTableAnalytics from '../components/DataTableAnalytics';
import { ToolTipFunction } from '../components/inputField';
import RagCost from '../components/Rag/RagCost';
import useCloudProvider from '../hooks/useCloudProvider';
import RagCostTable from '../components/Rag/RagCostTable';
import UnileverAnimation from '../components/UnileverAnimation';

const getDefaultDateRange = () => {
  const today = new Date();
  const pastDate = new Date();
  // pastDate.setMonth(today.getMonth() - 3);
  pastDate.setDate(today.getDate() - 6);
  return { fromDate: pastDate, toDate: today };
};

const truncateText = (text: string, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

interface DropdownValues {
  filters: {
    applications: string[];
    model_names: string[];
    granularity_options: string[];
  };
}

export default function CostAnalytics() {
  const { graphData, apiToken } = useAzureData();
  const { fromDate, toDate } = getDefaultDateRange();
  const [show, setShow] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: fromDate?.toLocaleDateString('en-GB'),
    to: toDate?.toLocaleDateString('en-GB')
  });
  const [choosedFilter, setChoosedFilter] = useState<any>('');
  const [currentchooseApp, setCurrentChoosedApp] = useState<any>('');
  const [cardData, setCardData] = useState<any>(null);
  const [costAnalyticsData, setCostAnalyticsData] = useState<any>(null);
  const [filterDropdownValues, setFilterDropdownvalues] = useState<DropdownValues | null>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dropdownValuesLoading, setDropdownValuesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const hasFetchedRef = useRef(false);
  const { selectCloud, selectedCloud, cloudProvidersList } = useCloudProvider();
  const [ragFilters, setRagFilters] = useState<any>(null);
  const [showRagTable, setShowRagTable] = useState<Boolean>(false);
  const [fetchedOtherRagCost, setFetchedOtherRagCost] = useState<Boolean>(false);

  const [cloudFilterID, setCloudFilter] = useState(0);

  const fetchData = async (appliedFilters: any) => {
    setLoading(true);
    try {
      let response = await fetchCostAnalyticsData(apiToken, appliedFilters);
      response &&
        response?.data?.metricsData?.length > 0 &&
        setCardData(response?.data?.metricsData);
      response && setCostAnalyticsData(response?.data);
      response && setTableData(response?.data?.applicationPromptCostSummary);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    setDropdownValuesLoading(true);
    try {
      let response = await filterDropdownData(
        apiToken,
        currentchooseApp ? currentchooseApp : '',
        cloudFilterID ? cloudFilterID : 0
      );
      response && setFilterDropdownvalues(response);
      setDropdownValuesLoading(false);
    } catch (error) {
      setDropdownValuesLoading(false);
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, [cloudFilterID]);

  useEffect(() => {
    if (hasFetchedRef?.current) return;
    hasFetchedRef.current = true;
    fetchData({});
    fetchDropdownData();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchDropdownData();
    //eslint-disable-next-line
  }, [currentchooseApp]);

  const vardDetails = [
    {
      id: 0,
      header: cardData && cardData[0]?.title,
      amount: cardData && cardData[0]?.value,
      description: cardData && cardData[0]?.subtitle,
      icon: <BriefCase />
    },
    {
      id: 1,
      header: cardData && cardData[1]?.title,
      amount: cardData && cardData[1]?.value,
      description: cardData && cardData[1]?.subtitle,
      icon: <Calculator />
    },
    {
      id: 2,
      header: cardData && cardData[2]?.title,
      amount: cardData && cardData[2]?.value,
      description: cardData && cardData[2]?.subtitle,
      icon: <Screw />
    },
    {
      id: 3,
      header: cardData && cardData[3]?.title,
      amount: cardData && cardData[3]?.value,
      description: cardData && cardData[3]?.subtitle,
      icon: <Graph />
    },
    {
      id: 4,
      header: cardData && cardData[4]?.title,
      amount: cardData && cardData[4]?.value,
      description: cardData && cardData[4]?.subtitle,
      icon: <Calender />
    }
  ];

  const GraphData = [
    {
      name: 'Cost Over Time',
      description: 'Fluctuation of cost across App over time.',
      graphType: 'LineChart',
      source: costAnalyticsData?.costOverTime
    },
    {
      name: 'Cost By Intercompany Cross Charge',
      description: 'Comparison of cost incurred across top 10 Intercompany Cross Charges(ICC).',
      graphType: 'onlyBaR',
      source: costAnalyticsData?.icc
    },
    {
      name: 'Cost By Cost Center',
      description: 'Comparison of cost incurred across top 10 Cost Centers(CC).',
      graphType: 'onlyBaR',
      source: costAnalyticsData?.cc
    },
    {
      name: 'App Cost Distribution Based on Cost Type',
      description: 'Comparison of ingestion and serving cost incurred across top 10 applications.',
      graphType: 'twoBaR',
      source: costAnalyticsData?.appCostDistribution
    },
    {
      name: 'Cost by Model',
      description: 'Comparison of cost incurred across top 10 LLM Models.',
      graphType: 'horizontalBar',
      source: costAnalyticsData?.costByModel
    },
    {
      name: 'Cost by Requester',
      description: 'Comparison of cost incurred across top 10 requesters.',
      graphType: 'horizontalBar',
      source: costAnalyticsData?.costByRequester
    },
    {
      name: 'Tokens by Model',
      description: 'Comparison of token used across top 10 LLM models.',
      graphType: 'LineChart',
      source: costAnalyticsData?.tokenByModel
    },
    {
      name: 'Tokens by Application',
      description: 'Comparison of token used across top 10 applications.',
      graphType: 'LineChart',
      source: costAnalyticsData?.tokenByApplication
    },
    {
      name: 'Cost by Environment',
      description: 'Split of total cost by different environments.',
      graphType: 'donutChart',
      source: costAnalyticsData?.costByEnvironment
    }
  ];

  let applicationNames =
    filterDropdownValues &&
    filterDropdownValues?.filters?.applications?.length > 0 &&
    filterDropdownValues?.filters?.applications?.map((item) => ({ label: item, value: item }));
  let modelNames =
    filterDropdownValues &&
    filterDropdownValues?.filters?.model_names?.length > 0 &&
    filterDropdownValues?.filters?.model_names?.map((item) => ({ label: item, value: item }));

  const filteredTableData = !search
    ? tableData?.rows
    : tableData?.rows?.filter((row: any) => {
        const searchTerm = search.toLowerCase();

        return (
          row.application_name?.toLowerCase().includes(searchTerm) ||
          row.model?.toLowerCase().includes(searchTerm) ||
          row.model_type?.toLowerCase().includes(searchTerm)
        );
      });

  const handleRagFilters = (filters: any) => {
    const servicenames = filters?.selectedServiceNames?.map((item: any) => item.value) || [];
    const vector_search_index_names =
      filters?.selectedVectorSearchNames?.map((item: any) => item.value) || [];
    const environment = filters?.selectedEnvironment?.value;
    const start_date = new Date(filters?.startDate).toISOString();
    const end_date = new Date(filters?.endDate).toISOString();
    let data = {
      servicenames,
      vector_search_index_names,
      environment,
      start_date,
      end_date,
      created_by: graphData?.['mail'] ?? ''
    };
    setRagFilters(data);
    setShowRagTable(false)
  };

  const requiredKeys = [
    'servicenames',
    'vector_search_index_names',
    'environment',
    'start_date',
    'end_date'
  ];

  const isValid =
    ragFilters &&
    requiredKeys?.every((key) => {
      const value = ragFilters[key];
      if (Array?.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== '';
    });

  return (
    <>
      <div style={{ padding: 20 }}>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <PageHeader
            headerName={'Cost Analysis'}
            description={
              'Gain visibility into spending trends, high-cost prompts, and app-wise model utilization to ensure cost-effective AI operations'
            }>
            <CircleDollarSign
              size={17}
              strokeWidth={1.5}
              color="var(--uni_std_clr)"
              style={{ marginRight: 5 }}
            />
          </PageHeader>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center" style={{ marginTop: '10%' }}>
            {/* <Spinner animation="border" variant="primary" /> */}
            <UnileverAnimation />
          </div>
        ) : (
          <>
            {/* Filter section */}
            <Row className="d-flex mb-3 ">
              <Col className="d-flex align-align-items-center">
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.application) || 'All Applications'}
                </div>
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.modelName) || 'All Models'}
                </div>

                {
                  <div className="d-flex align-items-center show_date_btn me-2">
                    {['Azure', 'GCP']?.[cloudFilterID - 1] || 'GCP and Azure'}
                  </div>
                }
                {choosedFilter && (
                  <div className="d-flex align-items-center gap-2 show_date_btn">
                    <img src={calenderIcon} alt="calender" />
                    {dateRange.from} - {dateRange.to}
                  </div>
                )}
              </Col>
              <Col className="d-flex justify-content-end">
                <ToolTipFunction
                  toolTipText={
                    <>
                      <p>
                        <b>Unless a filter is applied, the data shown will be as follows:</b>
                      </p>
                      <ul>
                        <li>
                          <b>Cards</b> - Cumulative data up to the current date
                        </li>
                        <li>
                          <b>Graphs/Tables</b> - Data from the last 7 days
                        </li>
                      </ul>
                    </>
                  }>
                  <Info />
                </ToolTipFunction>
                <Button
                  onClick={() => setShow(true)}
                  variant="outline-secondary"
                  className="d-flex align-items-center ms-2 gap-2 filterBtn">
                  <img src={filterIcon} alt="filter" />
                  Filter
                </Button>
              </Col>
            </Row>

            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {vardDetails?.map((item, i) => (
                <PlayGroundCard item={item} key={i} />
              ))}
            </div>

            <Row className="mb-1 mt-3" style={{ width: '250px' }}>
              <Col>
                <div className="d-flex align-items-center gap-2 show_date_btn">
                  <img src={calenderIcon} alt="calender" />
                  {dateRange.from} - {dateRange.to}
                </div>
              </Col>
            </Row>

            {/* Grapgh Section */}
            <div className="d-flex flex-wrap justify-content-between" style={{ gap: 10 }}>
              {GraphData?.map((item, index) => (
                <GraphSection key={index} item={item} index={index} />
              ))}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <p className="h3 w400 my-2">Application-Wise Prompt Cost Summary </p>
                </div>
                <div className="d-flex align-items-center">
                  <div className="search-box d-flex align-items-center" style={{ height: '38px' }}>
                    <Search size={18} strokeWidth={1.5} color="#838FB4" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {filteredTableData?.length > 0 ? (
                <DataTableAnalytics
                  header={TableData?.header}
                  data={filteredTableData}
                  itemOptions={[5, 10]}
                />
              ) : (
                <p className="text-center mt-3">No matching results found.</p>
              )}
            </div>

            <div className="d-flex align-items-center">
              <p className="h4 w400 mt-2 me-2">RAG Cost Analysis</p>
              <ToolTipFunction
                toolTipText={
                  <>
                    <p>
                      <b>
                        This chart is not affected by the above filter. Only the filters available
                        within this chart will apply
                      </b>
                    </p>
                  </>
                }>
                <Info />
              </ToolTipFunction>
            </div>
            {/* @ts-ignore */}
            <RagCost onFiltersChange={handleRagFilters} setFetchedOtherRagCost={setFetchedOtherRagCost} />
            {isValid && (
              <>
                <div className="d-flex align-items-center">
                  <p className="h4 w400 mt-2 me-2">RAG Cost Table View Analysis</p>
                  <ToolTipFunction
                    toolTipText={
                      <>
                        <p>
                          <b>
                            This Table is not affected by the above filter. Only the filters applied
                            on the Rag Cost Analysis Graph will be applicable
                          </b>
                        </p>
                      </>
                    }>
                    <Info />
                  </ToolTipFunction>
                  {!showRagTable && fetchedOtherRagCost &&
                    <Button className="unilever-btn-border1 w-20 ms-2"
                      onClick={() => setShowRagTable(true)}
                    >
                      View Table Analysis
                    </Button>
                  }
                </div>
                {showRagTable && <RagCostTable ragFilters={ragFilters} />}
              </>
            )}
          </>
        )}
      </div>

      <FilterForm
        show={show}
        onClose={() => setShow(false)}
        onApply={(filters) => {
          fetchData(filters);
          setChoosedFilter(filters);
          setCloudFilter(selectedCloud);
        }}
        showCloudProvider={true}
        selectedCloud={selectedCloud}
        cloudProvidersList={cloudProvidersList}
        selectCloud={selectCloud}
        onDateRangeChange={setDateRange}
        onCurrentChoosedAppChange={setCurrentChoosedApp}
        dropdownValuesLoading={dropdownValuesLoading}
        fields={[
          { key: 'timePeriod', label: 'Time Period', type: 'dateRange' },
          {
            key: 'application',
            label: 'Application',
            type: 'select',
            options: applicationNames ? applicationNames : []
          },
          {
            key: 'modelName',
            label: 'Model Name',
            type: 'select',
            options: modelNames ? modelNames : []
          }
        ]}
      />
    </>
  );
}
